import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useState } from 'react';
import { Search, ChevronLeft, ChevronRight, Bell, ArrowUpDown, Users } from 'lucide-react';
import type { Member, MemberFilters, MemberStatusFilter } from '../../types/memberships';
import { exportMembersToCSV } from '../../utils/membershipFormatters';

type SortOption = 'spending' | 'recent' | 'expiring' | 'name';

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'spending', label: 'Highest Spending' },
  { value: 'recent', label: 'Most Recent' },
  { value: 'expiring', label: 'Expiring Soon' },
  { value: 'name', label: 'Name (A-Z)' },
];

interface MembersTableProps {
  members: Member[];
  filters: MemberFilters;
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  onFilterChange: (filters: Partial<MemberFilters>) => void;
  onAddMember: () => void;
  onMemberClick: (member: Member) => void;
  onMemberHover?: (userId: number) => void;
  onSendReminder: (membershipId: number) => void;
  sendingReminderId: number | null;
  onSwitchToPackages: () => void;
}

const STATUS_TABS: { id: MemberStatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'expiring_soon', label: 'Expiring Soon' },
  { id: 'expired', label: 'Expired' },
  { id: 'canceled', label: 'Canceled' },
];

function getStatusBadge(member: Member) {
  // Canceled takes priority (check if has refund or cancellation reason)
  if (!member.is_active && (member.cancellation_reason || member.refund_amount)) {
    return { label: 'Canceled', className: 'bg-red-100 text-red-700 border-red-200' };
  }
  if (member.is_expiring_soon) {
    return { label: 'Expiring Soon', className: 'bg-amber-100 text-amber-700 border-amber-200' };
  }
  if (member.is_expired) {
    return { label: 'Expired', className: 'bg-gray-100 text-gray-600 border-gray-200' };
  }
  if (member.is_active) {
    return { label: 'Active', className: 'bg-green-100 text-green-700 border-green-200' };
  }
  return { label: 'Inactive', className: 'bg-gray-100 text-gray-600 border-gray-200' };
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-NG', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatPlanType(planType: string): string {
  return planType.charAt(0).toUpperCase() + planType.slice(1);
}

export function MembersTable({
  members,
  filters,
  pagination,
  onFilterChange,
  onAddMember,
  onMemberClick,
  onMemberHover,
  onSendReminder,
  sendingReminderId,
  onSwitchToPackages,
}: MembersTableProps) {
  const [searchInput, setSearchInput] = useState(filters.search);

  // Debounced search — trigger on Enter or blur
  const commitSearch = () => {
    if (searchInput !== filters.search) {
      onFilterChange({ search: searchInput, page: 1 });
    }
  };

  const canSendReminder = (member: Member) => {
    // Only show reminder bell for members expiring within 7 days
    return (
      member.is_expiring_soon &&
      member.days_remaining !== null &&
      member.days_remaining <= 7 &&
      member.days_remaining >= 0 &&
      member.user?.email &&
      !member.renewal_reminder_sent
    );
  };

  return (
    <>
      {/* Toolbar with View Mode, Search, and Sort */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* View Mode Buttons */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={onSwitchToPackages}
              className="px-3 sm:px-4 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all whitespace-nowrap bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Packages
            </button>
            <button
              className="px-3 sm:px-4 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all whitespace-nowrap bg-blue-600 text-white"
            >
              Members
            </button>
          </div>

          {/* Search and Sort */}
          <div className="flex flex-1 items-center gap-2">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by name, email, or membership ID..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && commitSearch()}
                onBlur={commitSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 text-xs sm:text-sm"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="relative">
              <ArrowUpDown className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select
                value={filters.sort || 'recent'}
                onChange={(e) => onFilterChange({ sort: e.target.value as SortOption, page: 1 })}
                className="pl-9 pr-8 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 text-xs sm:text-sm appearance-none bg-white cursor-pointer whitespace-nowrap"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Member Count */}
            <span className="text-xs sm:text-sm text-gray-500 hidden lg:inline whitespace-nowrap">
              {pagination.total} member{pagination.total !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 mb-4">
        <div className="flex items-center overflow-x-auto border-b border-gray-200 px-3 sm:px-4">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onFilterChange({ status: tab.id, page: 1 })}
              className={`px-3 sm:px-5 py-3 font-semibold text-xs sm:text-sm transition-all border-b-2 whitespace-nowrap ${
                filters.status === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200">
        {members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Users className="w-12 h-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">No members found</h3>
            <p className="text-gray-500 text-sm text-center">
              {filters.status !== 'all' || filters.search
                ? 'Try adjusting your filters or search query.'
                : 'Members will appear here when customers purchase memberships.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[850px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Member</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Package</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Plan</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Your Earnings</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Start</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">End</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Days Left</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => {
                    const badge = getStatusBadge(member);
                    const isSending = sendingReminderId === member.id;
                    return (
                      <tr
                        key={member.id}
                        onClick={() => onMemberClick(member)}
                        onMouseEnter={() => onMemberHover?.(member.user.id)}
                        className="border-b border-gray-50 hover:bg-blue-50 transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-bold text-gray-900 text-sm">{member.user?.name || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">
                              {member.user?.email && !member.user.email.includes('@spotts.local')
                                ? member.user.email
                                : '—'}
                            </p>
                            {member.user?.phone && (
                              <p className="text-gray-400 text-xs">{member.user.phone}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-gray-700 text-sm font-medium">
                              {member.package?.package_name || '—'}
                            </span>
                            {member.total_memberships_count > 1 && (
                              <span className="text-xs text-blue-600 font-semibold mt-0.5">
                                + {member.total_memberships_count - 1} more
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-700 text-sm">
                            {formatPlanType(member.plan_type)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold ${
                            member.payment_reference?.startsWith('MM-')
                              ? 'bg-green-100 text-green-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {member.payment_reference?.startsWith('MM-') ? 'Manual' : 'Spotts'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-black text-gray-900 text-base">
                            {CURRENCY_SYMBOL}{Math.floor(Number(member.total_venue_share)).toLocaleString()}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-bold border ${badge.className}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-700 text-sm">{formatDate(member.start_date)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-700 text-sm">{formatDate(member.end_date)}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {!member.is_active && (member.cancellation_reason || member.refund_amount) ? (
                            <span className="text-sm text-gray-400">—</span>
                          ) : (
                            <span className={`text-sm font-bold ${
                              member.days_remaining <= 3 && member.days_remaining > 0
                                ? 'text-amber-600'
                                : member.days_remaining === 0
                                  ? 'text-red-500'
                                  : 'text-gray-700'
                            }`}>
                              {member.is_expired ? '0' : member.days_remaining}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          {canSendReminder(member) ? (
                            <button
                              onClick={() => onSendReminder(member.id)}
                              disabled={isSending}
                              title="Send expiry reminder email"
                              className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 disabled:opacity-50 transition-colors"
                            >
                              <Bell className={`w-4 h-4 ${isSending ? 'animate-pulse' : ''}`} />
                            </button>
                          ) : member.renewal_reminder_sent ? (
                            <span title="Reminder already sent" className="p-1.5 inline-block text-gray-300">
                              <Bell className="w-4 h-4" />
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.last_page > 1 && (
              <div className="px-4 py-4 border-t border-gray-100 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Page {pagination.current_page} of {pagination.last_page}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onFilterChange({ page: pagination.current_page - 1 })}
                    disabled={pagination.current_page <= 1}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onFilterChange({ page: pagination.current_page + 1 })}
                    disabled={pagination.current_page >= pagination.last_page}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
